import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateWarehousePositionRequest,
    ListWarehousePositionsParams,
    UpdateWarehousePositionRequest,
} from '@/lib/api/warehouse-position-api'
import { warehousePositionApi } from '@/lib/api/warehouse-position-api'
import { m } from '@/lib/paraglide/messages'
import { warehousePositionErrorToast } from '@/lib/warehouse-position-error-toast'

export const warehousePositionKeys = {
    all: ['warehouse-positions'] as const,
    list: (params: ListWarehousePositionsParams) =>
        [...warehousePositionKeys.all, 'list', params] as const,
    detail: (id: string) =>
        [...warehousePositionKeys.all, 'detail', id] as const,
}

export function useWarehousePositions(
    params: ListWarehousePositionsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: warehousePositionKeys.list(params),
        queryFn: () => warehousePositionApi.list(params),
    })
}

export function useWarehousePosition(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: warehousePositionKeys.detail(id ?? ''),
        queryFn: () => warehousePositionApi.getById(id as string),
    })
}

export function useCreateWarehousePosition() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateWarehousePositionRequest) =>
            warehousePositionApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: warehousePositionKeys.all })
            toast.success(m.warehouse_position_toast_created())
        },
        onError: (err: Error) => {
            const { title, description } = warehousePositionErrorToast(
                err,
                m.warehouse_position_toast_create_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useUpdateWarehousePosition(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpdateWarehousePositionRequest) =>
            warehousePositionApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: warehousePositionKeys.all })
            toast.success(m.warehouse_position_toast_updated())
        },
        onError: (err: Error) => {
            const { title, description } = warehousePositionErrorToast(
                err,
                m.warehouse_position_toast_update_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useDeleteWarehousePosition() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => warehousePositionApi.remove(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: warehousePositionKeys.all })
            toast.success(m.warehouse_position_toast_deleted())
        },
        onError: (err: Error) =>
            toast.error(
                err.message || m.warehouse_position_toast_delete_error(),
            ),
    })
}
